import { useCallback, useEffect, useMemo } from 'react'
import type { SearchOption } from '@jana-technology/platform-ui'

import { ICodeNamePair } from '$/types'
import { useGetAcceptanceCategoriesQuery } from 'app/services/api/acceptanceCategories'
import {
  findLookupTablesFromBaseSettings,
  useGetBaseSettingsQuery
} from 'app/services/api/basesettings'
import { useGetActiveSystemScopeFilterProfileQuery } from 'app/services/api/filterprofiles'
import OverlayScrollbar from 'common/components/OverlayScrollBar'

import { EMPTY_ROUTE } from 'features/RiskManagement/Components/LocalFilter'
import { LocalFilterTypes } from 'features/RiskManagement/Components/LocalFilter/Modal/LocalFilterTypes'
import { NetworkDropdown } from 'features/RiskManagement/Components/LocalFilter/Modal/NetworkDropdown'

import { IFilterDetailsProps } from './types'

interface ISelectionsMemo {
  classLocationOptions: SearchOption[]
  riskLevelOptions: SearchOption[]
  consequenceOptions: SearchOption[]
}

const FilterDetails = ({
  localFilter,
  selectedRoutes,
  setSelectedRoutes,
  selectedHmca,
  onHMCASelection,
  selectedClassLocations,
  onClassLocationSelection,
  selectedRiskLevels,
  onRiskLevelSelection,
  setIsLocalFilterStateChanged,
  paramsRouteId,
  setCanFilterBeApplied
}: IFilterDetailsProps) => {
  const { systemScopeFilterProfile } =
    useGetActiveSystemScopeFilterProfileQuery()

  const { lookupTables } = useGetBaseSettingsQuery(undefined, {
    selectFromResult: ({ data }) => ({
      lookupTables: findLookupTablesFromBaseSettings(data)
    })
  })

  const { data: riskThresholds } = useGetAcceptanceCategoriesQuery()

  const { classLocationOptions, riskLevelOptions, consequenceOptions } =
    useMemo(() => {
      const selectOptions: ISelectionsMemo = {
        classLocationOptions: [],
        riskLevelOptions: [],
        consequenceOptions: []
      }

      const mapCodeNamePairsToValueLables = (codeNamePairs: ICodeNamePair[]) =>
        codeNamePairs.map((pair) => ({
          value: String(pair.Code),
          label: pair.Name
        }))

      if (lookupTables) {
        lookupTables.details.forEach((detail) => {
          const codeNamePairs = detail.value as ICodeNamePair[]

          if (detail.key === LocalFilterTypes.ClassLocation) {
            selectOptions.classLocationOptions =
              mapCodeNamePairsToValueLables(codeNamePairs)
          }

          if (detail.key === LocalFilterTypes.HMCA) {
            selectOptions.consequenceOptions =
              mapCodeNamePairsToValueLables(codeNamePairs)
          }
        })
      }

      if (riskThresholds) {
        selectOptions.riskLevelOptions = mapCodeNamePairsToValueLables(
          riskThresholds.map((threshold) => ({
            Code: threshold.code,
            Name: threshold.name
          }))
        )
      }

      return selectOptions
    }, [lookupTables, riskThresholds])

  // Populates the route options. If in route view, only the current route is added
  const routeOptions = useMemo(() => {
    if (systemScopeFilterProfile) {
      if (paramsRouteId != null) {
        const selectedRoute = systemScopeFilterProfile.continuousRoutes.find(
          (route) => String(route.id) === paramsRouteId
        )

        if (selectedRoute) {
          return [
            {
              label: selectedRoute.name,
              value: String(selectedRoute.id)
            }
          ]
        }
      } else {
        return systemScopeFilterProfile.continuousRoutes.map((route) => ({
          label: route.name,
          value: String(route.id)
        }))
      }
    }

    return []
  }, [paramsRouteId, systemScopeFilterProfile])

  useEffect(() => {
    const canApply = selectedRoutes.every((route) => {
      // Math.abs for negative fromMeasure; assuming initialFromMeasure is 0
      const canApplyFromMeasure =
        Math.abs(route.fromMeasure) >= route.initialFromMeasure

      const canApplyToMeasure = route.toMeasure <= route.initialToMeasure
      const isFromLessThanToMeasure = route.fromMeasure <= route.toMeasure

      return canApplyFromMeasure && canApplyToMeasure && isFromLessThanToMeasure
    })

    setCanFilterBeApplied(canApply)
  }, [selectedRoutes, setCanFilterBeApplied])

  // instead of tracking row indices separately, empty routes are negatives
  // we need index for swapping empty routes with selections onChange/selectRoute
  const addRoute = useCallback(() => {
    setSelectedRoutes((state) =>
      state.concat({
        ...EMPTY_ROUTE,
        rowId: Math.random(),
        id: state.length * -1 - 1,
        continuousRouteId: state.length * -1 - 1,
        pipelineRouteId: state.length * -1 - 1
      })
    )

    setIsLocalFilterStateChanged(true)
  }, [setIsLocalFilterStateChanged, setSelectedRoutes])

  const removeRoute = useCallback(
    (routeId: number) => {
      setSelectedRoutes((state) => {
        const filteredRoutes = state.filter(
          (selectedRoute) => selectedRoute.rowId !== routeId
        )

        return filteredRoutes.length ? filteredRoutes : [EMPTY_ROUTE]
      })

      setIsLocalFilterStateChanged(true)
    },
    [setIsLocalFilterStateChanged, setSelectedRoutes]
  )

  // ! currently selecting duplicate routes is enabled
  const selectRoute = useCallback(
    (selections: SearchOption[], index: number) => {
      if (selections?.length) {
        const [selection] = selections
        const id = Number(selection.value)
        const name = selection.label

        const targetRange = systemScopeFilterProfile?.continuousRoutes.find(
          (route) => route.id === id
        )

        const { fromMeasure = 0, toMeasure = 1000 } = targetRange || {}
        const roundedFromMeasure = Math.floor(fromMeasure)
        const roundedToMeasure = Math.ceil(toMeasure)

        setSelectedRoutes((state) =>
          state.map((selectedRoute) => {
            if (selectedRoute.rowId === index) {
              return {
                name,
                rowId: index,
                id,
                pipelineRouteId: id,
                continuousRouteId: id,
                fromMeasure: roundedFromMeasure,
                toMeasure: roundedToMeasure,
                initialFromMeasure: roundedFromMeasure,
                initialToMeasure: roundedToMeasure
              }
            }

            return selectedRoute
          })
        )

        setIsLocalFilterStateChanged(true)
      }
    },
    [
      setIsLocalFilterStateChanged,
      setSelectedRoutes,
      systemScopeFilterProfile?.continuousRoutes
    ]
  )

  const updateRouteRange = useCallback(
    (routeId: number, range: number[]) => {
      setSelectedRoutes((state) =>
        state.map((selectedRoute) => {
          if (selectedRoute.rowId === routeId) {
            return {
              ...selectedRoute,
              fromMeasure: range[0],
              toMeasure: range[1]
            }
          }

          return selectedRoute
        })
      )

      setIsLocalFilterStateChanged(true)
    },
    [setIsLocalFilterStateChanged, setSelectedRoutes]
  )

  return (
    <OverlayScrollbar>
      <NetworkDropdown
        localFilter={localFilter}
        routeOptions={routeOptions}
        selectedRoutes={selectedRoutes}
        setSelectedRoutes={setSelectedRoutes}
        selectedHmca={selectedHmca}
        onHMCASelection={onHMCASelection}
        onClassLocationSelection={onClassLocationSelection}
        selectedRiskLevels={selectedRiskLevels}
        onRiskLevelSelection={onRiskLevelSelection}
        addRoute={addRoute}
        removeRoute={removeRoute}
        selectRoute={selectRoute}
        updateRouteRange={updateRouteRange}
        classLocationOptions={classLocationOptions}
        riskLevelOptions={riskLevelOptions}
        consequenceOptions={consequenceOptions}
        selectedClassLocations={selectedClassLocations}
      />
    </OverlayScrollbar>
  )
}

export default FilterDetails
